import { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "../../../../theme";
import { SidePanelSection } from "../../../../components/SidePanel";
import { protocolLabel, identityFieldsFor, identityFieldLabel } from "./protocolIdentityCatalog";
import TruncatedText from "./TruncatedText";

// Splits on "," at nesting depth 0 only — a comma inside "(...)" (User-Agent
// comment blocks routinely have one: "(KHTML, like Gecko)") stays attached to
// its parenthetical instead of breaking the string in a nonsensical place.
const splitOutsideParens = (text) => {
  const parts = [];
  let depth = 0;
  let current = "";
  for (const ch of text) {
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter(Boolean);
};

// Field(s) verbose enough that a comma-separated breakdown earns its own
// list rendering instead of one long line — today just the HTTP User-Agent
// banner, the one raw field this section shows that's routinely a full
// sentence rather than a single token.
const LIST_FIELD_KEYS = new Set(["user_agent"]);
const LIST_VISIBLE_LIMIT = 6;

// `alwaysExpanded` is how the detail dialog opts every value on the page out
// of truncation at once — no per-item "Show more", no "+N more" list cutoff,
// everything the way a "show me everything" view should behave by default.
const CommaListValue = ({ mono, value, alwaysExpanded }) => {
  const [showAll, setShowAll] = useState(false);
  const parts = splitOutsideParens(value == null ? "" : String(value));
  const visible = alwaysExpanded || showAll ? parts : parts.slice(0, LIST_VISIBLE_LIMIT);
  const hidden = parts.length - visible.length;

  return (
    <Box>
      <Box component="ul" sx={{ m: 0, pl: 1.4, listStyle: "disc" }}>
        {visible.map((part, i) => (
          <Box component="li" key={i} sx={{ "&::marker": { color: colors.grey[100], fontSize: 8 }, mb: 0.9, "&:last-of-type": { mb: 0 } }}>
            <TruncatedText mono={mono} clampLines={1} alwaysExpanded={alwaysExpanded}>{part}</TruncatedText>
          </Box>
        ))}
      </Box>
      {hidden > 0 && (
        <Typography
          onClick={() => setShowAll(true)}
          sx={{ fontSize: 10, color: "#459095", cursor: "pointer", fontWeight: 600, mt: 0.2 }}
        >
          +{hidden} more
        </Typography>
      )}
    </Box>
  );
};

// Label and value side by side, not stacked — a stacked row (label line, then
// a value line under it) cost two lines of height for even a two-character
// value like a VLAN tag. A fixed narrow label column halves that for every
// short field, which is most of them; a label that doesn't fit (rare —
// "Server DNS Computer Name") just wraps onto a second line within its own
// column instead of forcing every OTHER field's label onto two lines as well.
// Nothing is cut: a long *value* still gets TruncatedText's clamp-plus-"Show
// more", or — for the User-Agent field — CommaListValue's per-clause list.
//
// `labelColor` defaults to a shade tuned for the card's near-black
// background; the dialog's paper is a much lighter grey (MuiDialog override,
// theme.js) and that same shade nearly disappears into it, so the dialog
// passes a lighter one explicitly — still one step darker than the value so
// the two stay visually distinct.
//
// `py` defaults to the card's tight spacing (fields have to fit CARD_HEIGHT
// there); the dialog isn't height-constrained the same way, so it passes a
// roomier value — one row of breathing room shouldn't cost the compact
// preview its fixed size.
const IdentityRow = ({ label, fieldKey, mono, value, alwaysExpanded = false, labelColor = colors.grey[500], py = 0.3 }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "68px 1fr",
      gap: 0.8,
      py,
      borderBottom: `1px dashed ${colors.grey[800]}`,
      "&:last-of-type": { borderBottom: "none" },
      alignItems: "start",
    }}
  >
    <Typography sx={{ fontSize: 9, color: labelColor, textTransform: "uppercase", letterSpacing: 0.3, lineHeight: 1.25, pt: 0.15 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 12, color: colors.grey[100], minWidth: 0 }}>
      {LIST_FIELD_KEYS.has(fieldKey) ? (
        <CommaListValue mono={mono} value={value} alwaysExpanded={alwaysExpanded} />
      ) : (
        <TruncatedText mono={mono} alwaysExpanded={alwaysExpanded}>{value}</TruncatedText>
      )}
    </Box>
  </Box>
);

// Every card this size, no matter whether the protocol carries two fields or
// twelve — the point of a scroll row of same-shaped cards is that they scan
// as one set; a card that grows with its own content breaks that the moment
// one protocol happens to be more talkative than its neighbours.
const CARD_HEIGHT = 168;
// How many rows comfortably fit CARD_HEIGHT below the title. A protocol with
// more than this gets a "+N more" row in its place — cut short on purpose,
// not by accident: the full set is always one click away in the dialog.
const CARD_VISIBLE_FIELDS = 4;

// `protocols{}` is an open set — the probe attaches identity to whichever protocol
// keys are actually seen (Modbus, S7, BACnet, DICOM, ONVIF, SNMP ENTITY-MIB, an IPP
// printer, NTLM/SMB/HTTP banners, ...), and a single asset can legitimately carry
// several at once. A fixed set of hand-written sections doesn't fit that: it either
// hides new protocols or needs a code change every time the probe adds one. So this
// renders whatever is actually present, one compact card per protocol, laid out as
// a horizontally scrollable row (two cards at a time) rather than a long vertical
// stack or a wrapping grid — N protocols costs a scroll gesture, not N times the
// height of a flat list.
const ProtocolCard = ({ protoKey, block, onOpen }) => {
  const fields = identityFieldsFor(protoKey, block).filter(([key]) => {
    const v = block[key];
    return v != null && v !== "";
  });
  if (fields.length === 0) return null;

  const overflow = fields.length > CARD_VISIBLE_FIELDS;
  const visible = overflow ? fields.slice(0, CARD_VISIBLE_FIELDS - 1) : fields;
  const hiddenCount = fields.length - visible.length;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(protoKey, block)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(protoKey, block); } }}
      sx={{
        flex: { xs: "0 0 82%", sm: "0 0 calc(50% - 4px)" },
        scrollSnapAlign: "start",
        height: CARD_HEIGHT,
        overflow: "hidden",
        borderRadius: 1.5,
        backgroundColor: "#1c1c1c",
        border: `1px solid ${colors.grey[800]}`,
        p: 1.2,
        minWidth: 0,
        cursor: "pointer",
        "&:hover": { borderColor: "#459095", backgroundColor: "#212121" },
        "&:focus-visible": { outline: "2px solid #459095", outlineOffset: 1 },
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#7de8ed", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.6 }}>
        {protocolLabel(protoKey)}
      </Typography>
      {visible.map(([key, mono]) => (
        <IdentityRow key={key} fieldKey={key} label={identityFieldLabel(key)} mono={mono} value={block[key]} />
      ))}
      {overflow && (
        <Typography sx={{ fontSize: 10.5, color: "#459095", fontWeight: 600, pt: 0.5 }}>
          +{hiddenCount} more field{hiddenCount > 1 ? "s" : ""} — click to view all
        </Typography>
      )}
    </Box>
  );
};

// Scrolls by one "page" (~2 cards) at a time rather than by a fixed pixel
// amount, so the button always lands on a clean card boundary regardless of
// the container's actual width.
const ScrollButton = ({ direction, onClick, sx }) => (
  <IconButton
    size="small"
    onClick={onClick}
    sx={{
      position: "absolute",
      top: "50%",
      [direction === "left" ? "left" : "right"]: 4,
      transform: "translateY(-50%)",
      zIndex: 1,
      width: 26,
      height: 26,
      bgcolor: "#1c1c1cee",
      border: `1px solid ${colors.grey[700]}`,
      color: colors.grey[200],
      "&:hover": { bgcolor: "#2a2a2a", color: "#7de8ed" },
      ...sx,
    }}
  >
    {direction === "left" ? <ChevronLeftIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
  </IconButton>
);

// `snmp`/`dhcp` used to have their own dedicated section elsewhere on this tab
// and were excluded here to avoid showing the same data twice — those dedicated
// sections are gone now, so both flow through this generic card list like every
// other protocol. `lldp`/`cdp` stay excluded for an unrelated reason: verified
// against the probe's source, they never carry more than a capabilities bitmap
// (no vendor/model), so a card for them would be empty. Every other protocol
// key — including the raw-banner ones (NTLM, SMB, SSH, HTTP, FTP, Telnet,
// MySQL) that used to be silently dropped here on the assumption they were
// shown elsewhere, when in fact nothing else in the dashboard shows them —
// gets a card.
const DUPLICATED_ELSEWHERE = new Set(["lldp", "cdp"]);

export const ProtocolIdentitySection = ({ protocols }) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Which card's full field list is open in the dialog — null when closed.
  const [openProto, setOpenProto] = useState(null);

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  // Whether there's a second page to scroll to depends on the row's actual
  // rendered width, which isn't known until after layout — measure right
  // after mount/update (before paint, so no flash) rather than guessing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    updateScrollState();
  });

  // Requires at least one non-empty identity field, not just a non-empty
  // object: a block whose only populated keys are internal/metadata ones
  // would otherwise pass this filter and reach the section as a card with
  // nothing to show (ProtocolCard already returns null for it), leaving the
  // section header rendered above an empty row.
  const entries = Object.entries(protocols || {}).filter(([key, block]) => {
    if (DUPLICATED_ELSEWHERE.has(key) || !block || typeof block !== "object") return false;
    return identityFieldsFor(key, block).some(([fieldKey]) => block[fieldKey] != null && block[fieldKey] !== "");
  });
  if (entries.length === 0) return null;

  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <SidePanelSection title={`Protocol Identity (${entries.length})`}>
      <Typography sx={{ fontSize: 11, color: colors.grey[600], fontStyle: "italic", mb: 1 }}>
        What each protocol the device speaks says about itself — an asset can answer
        several at once, each with its own vendor/model/serial or banner.
      </Typography>
      <Box sx={{ position: "relative" }}>
        <Box
          ref={scrollerRef}
          onScroll={updateScrollState}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            pb: 0.5,
            // Dark and thin rather than the browser's default light-grey/white
            // bar, which read as a bright stripe against these dark cards.
            scrollbarWidth: "thin",
            scrollbarColor: "#3a3a3a transparent",
            "&::-webkit-scrollbar": { height: 5 },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#3a3a3a", borderRadius: 4 },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#4a4a4a" },
          }}
        >
          {entries.map(([key, block]) => (
            <ProtocolCard key={key} protoKey={key} block={block} onOpen={(k, b) => setOpenProto({ key: k, block: b })} />
          ))}
        </Box>
        {/* Two redundant cues that there's more off-screen: a card peeking in at
            the edge (via the < 100%-wide flex-basis on ProtocolCard) and this
            arrow — the peek alone is easy to miss, the arrow alone doesn't say
            which direction, together they do. */}
        {canScrollLeft && <ScrollButton direction="left" onClick={() => scrollByPage(-1)} />}
        {canScrollRight && <ScrollButton direction="right" onClick={() => scrollByPage(1)} />}
      </Box>

      <Dialog open={!!openProto} onClose={() => setOpenProto(null)} maxWidth="xs" fullWidth>
        {openProto && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: "#7de8ed" }}>
              {protocolLabel(openProto.key)}
              <IconButton size="small" onClick={() => setOpenProto(null)} sx={{ color: colors.grey[400] }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 0,
                maxHeight: "70vh",
                overflowY: "auto",
                // Same dark, thin treatment as the card row's scrollbar
                // (ProtocolIdentitySection's horizontal scroller) — the
                // browser's default light one would stand out against this
                // dark dialog otherwise.
                scrollbarWidth: "thin",
                scrollbarColor: "#3a3a3a transparent",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#3a3a3a", borderRadius: 4 },
                "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#4a4a4a" },
              }}
            >
              {identityFieldsFor(openProto.key, openProto.block)
                .filter(([fieldKey]) => openProto.block[fieldKey] != null && openProto.block[fieldKey] !== "")
                .map(([fieldKey, mono]) => (
                  <IdentityRow key={fieldKey} fieldKey={fieldKey} label={identityFieldLabel(fieldKey)} mono={mono} value={openProto.block[fieldKey]} alwaysExpanded labelColor={colors.grey[300]} py={1.2} />
                ))}
            </DialogContent>
          </>
        )}
      </Dialog>
    </SidePanelSection>
  );
};

export default ProtocolIdentitySection;
